import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { ArrowUp, ArrowDown, Clock, Activity, Star, Flame } from "lucide-react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { useActiveCurrencies } from "@/lib/marketSessions";
import { toast } from "sonner";
import CandleCountdown from "./CandleCountdown";

export default function LiveSignalCard({ signal, index = 0, timeframe, stats }) {
  const { t, pn, fmt } = useI18n();
  const { user, favorites, toggleFavorite } = useAuth();
  const activeCurrencies = useActiveCurrencies();
  const { symbol, signal: sig, confidence, price, change_pct, rsi, last_candle,
          style_key, expiry_seconds } = signal;
  const isUp = sig === "buy";
  const isDown = sig === "sell";
  const isHold = sig === "hold";
  const [from, to] = symbol.split("/");
  // "Hot" when either currency of the pair is part of a currently-open session.
  // USDT is always considered active (crypto 24/7).
  const isHot = activeCurrencies.has(from) || activeCurrencies.has(to) || to === "USDT" || from === "BTC";

  const color = isUp ? "emerald" : isDown ? "red" : "amber";
  const colorClasses = {
    emerald: "border-emerald-500/40 hover:border-emerald-500/70 from-emerald-500/10",
    red: "border-red-500/40 hover:border-red-500/70 from-red-500/10",
    amber: "border-amber-500/40 hover:border-amber-500/70 from-amber-500/10",
  }[color];
  const arrowClasses = {
    emerald: "bg-emerald-500/15 text-emerald-400",
    red: "bg-red-500/15 text-red-400",
    amber: "bg-amber-500/15 text-amber-400",
  }[color];

  return (
    <Link
      to={`/pair/${from}/${to}`}
      data-testid={`live-card-${from}${to}`}
      className="block animate-fade-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <Card className={cn(
        "relative bg-gradient-to-br to-[#121212] border bg-[#121212] rounded-sm p-5 overflow-hidden transition-colors",
        colorClasses
      )}>
        {!isHold && (
          <div className="absolute top-3 start-3">
            <div className={cn("relative h-2 w-2 rounded-full", isUp ? "bg-emerald-400" : "bg-red-400")}>
              <div className={cn("absolute inset-0 rounded-full animate-ping", isUp ? "bg-emerald-400" : "bg-red-400")} />
            </div>
          </div>
        )}

        <div className="flex items-start justify-between mb-4">
          <div className="space-y-0.5 flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (!user) { toast.info(t("login_required")); return; }
                  toggleFavorite(symbol);
                }}
                data-testid={`favorite-${from}${to}`}
                className={cn(
                  "p-0.5 rounded-sm transition-colors",
                  favorites?.has(symbol)
                    ? "text-amber-400 hover:text-amber-300"
                    : "text-zinc-600 hover:text-zinc-400"
                )}
                title={user ? "" : t("login_required")}
              >
                <Star className={cn("h-4 w-4", favorites?.has(symbol) && "fill-amber-400")} />
              </button>
              <div className="ticker text-lg font-bold text-white" data-testid={`live-symbol-${from}${to}`}>
                <span dir="ltr">{symbol}</span>
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 truncate">{pn(symbol)}</div>
          </div>
          {stats && stats.trades > 0 && (
            <div className="text-right" data-testid={`winrate-${from}${to}`}>
              <div className={cn("num text-[11px] font-bold",
                stats.win_rate >= 60 ? "text-emerald-400" : stats.win_rate >= 45 ? "text-zinc-300" : "text-red-400"
              )}>{stats.win_rate}% {t("winrate")}</div>
              <div className="text-[9px] text-zinc-600 num">{stats.trades} {t("trades")}</div>
            </div>
          )}
        </div>

        <div className="flex items-center gap-4 mb-5">
          <div className={cn("w-14 h-14 rounded-sm flex items-center justify-center", arrowClasses)}>
            {isUp && <ArrowUp className="h-8 w-8" strokeWidth={2.5} />}
            {isDown && <ArrowDown className="h-8 w-8" strokeWidth={2.5} />}
            {isHold && <Activity className="h-8 w-8" strokeWidth={2.5} />}
          </div>
          <div>
            <div className={cn("text-2xl font-bold tracking-tight",
              isUp && "text-emerald-400", isDown && "text-red-400", isHold && "text-amber-400"
            )} data-testid={`live-direction-${from}${to}`}>
              {isUp ? t("buy") : isDown ? t("sell") : t("hold")}
            </div>
            <div className="text-[11px] text-zinc-500">
              {t("confidence")} <span className="num text-white font-bold">{confidence}%</span>
            </div>
          </div>
        </div>

        <div className="flex items-end justify-between mb-4">
          <div className="num text-2xl font-bold text-white" dir="ltr">
            {price.toFixed(price > 50 ? 3 : 5)}
          </div>
          <div className={cn("num text-sm font-bold", change_pct >= 0 ? "text-emerald-400" : "text-red-400")} dir="ltr">
            {change_pct >= 0 ? "+" : ""}{change_pct.toFixed(3)}%
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 py-2">
            <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mb-1">
              <Clock className="h-3 w-3" /> {t("trade_duration")}
            </div>
            <div className="text-sm font-bold text-white">{fmt(expiry_seconds)}</div>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 py-2">
            <div className="text-[10px] text-zinc-500 mb-1">{t("trade_type")}</div>
            <div className="text-sm font-bold text-white">{t(style_key)}</div>
          </div>
        </div>

        <div className="space-y-1.5 pt-3 border-t border-zinc-800/60">
          <div className="flex items-center justify-between text-[10px] text-zinc-500">
            <span>{t("last_candle")}</span>
            <span className={cn("font-bold", last_candle.bullish ? "text-emerald-400" : "text-red-400")}>
              {last_candle.bullish ? t("bullish") : t("bearish")}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2 num text-[11px]" dir="ltr">
            <CandleStat label="O" value={last_candle.open} />
            <CandleStat label="H" value={last_candle.high} hi />
            <CandleStat label="L" value={last_candle.low} lo />
            <CandleStat label="C" value={last_candle.close} bullish={last_candle.bullish} />
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-zinc-800/60 flex items-center justify-between text-[11px]">
          <span className="text-zinc-500">RSI</span>
          <span className={cn("num font-bold",
            rsi < 30 ? "text-emerald-400" : rsi > 70 ? "text-red-400" : "text-zinc-300"
          )}>{rsi.toFixed(1)}</span>
        </div>

        {timeframe && (
          <div className="mt-3 pt-3 border-t border-zinc-800/60">
            <CandleCountdown timeframe={timeframe} />
          </div>
        )}
      </Card>
    </Link>
  );
}

function CandleStat({ label, value, hi, lo, bullish }) {
  return (
    <div className="text-center">
      <div className="text-[9px] text-zinc-600">{label}</div>
      <div className={cn("font-bold tabular-nums",
        hi && "text-emerald-300", lo && "text-red-300",
        bullish === true && "text-emerald-400", bullish === false && "text-red-400",
      )}>
        {value.toFixed(value > 50 ? 3 : 5)}
      </div>
    </div>
  );
}
